import { BellPlus, Clock, Printer, Stethoscope } from 'lucide-react'
import { useState } from 'react'

import {
  getAdministeredBatches,
  getPrimaryOrder,
  isVaccineTreatment,
  REGIONS_BY_ID,
  type TreatmentWithRelations,
} from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { VaccineCertificateDialog } from '@/frontend/features/documents/vaccine-certificate.form'
import { TreatmentAttributes } from '@/frontend/features/patient/timeline/treatment-attributes'
import { canAddReminder, TreatmentReminders } from '@/frontend/features/patient/treatment-reminders'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useGetPatient } from '@/frontend/hooks/api/patients/use-get-patient'
import { useListPatientTreatments } from '@/frontend/hooks/api/patients/use-list-patient-treatments'

/** Sidebar card showing a patient's vaccination history. */
export function VaccinationsCard({ patientId }: { patientId: string }) {
  const { data: allTreatments } = useListPatientTreatments(patientId)
  const vaccinations = allTreatments?.filter((t) => !t.cancelledAt && isVaccineTreatment(t))

  const { data: patient } = useGetPatient(patientId, (r) => r.patient)
  const { region } = useViewerRegion()
  const [certifying, setCertifying] = useState<TreatmentWithRelations | null>(null)
  const [addingReminderFor, setAddingReminderFor] = useState<string | null>(null)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Vaccinations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!vaccinations || vaccinations.length === 0 ? (
          <p className="text-muted-foreground">No vaccination records.</p>
        ) : (
          vaccinations.map((tx) => {
            // A treatment's clinic is the one it was dispensed at, via its primary
            // order — null when it has no dispensing order (the historical
            // catalog-only case), which getPrimaryOrder already returns on its own.
            const orderClinicId = getPrimaryOrder(tx.orders)?.clinicId ?? null
            const patientSpecies = patient?.species ?? ''

            return (
              <div
                key={tx.id}
                className="flex flex-col gap-1 rounded-lg border bg-muted/40 px-3 py-2.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="font-medium">{tx.productInfo.name}</p>
                    {tx.treatmentType === 'vaccination' && tx.catalogProduct && (
                      <Badge variant="outline" size="sm">
                        Historical
                      </Badge>
                    )}
                  </div>
                  {/* Clamped to the heading's line height so the icon buttons don't
                      stretch the row. */}
                  <div className="flex h-5 shrink-0 items-center">
                    {canAddReminder(
                      tx,
                      patientSpecies,
                      REGIONS_BY_ID[region].vaccineTargetMode
                    ) && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        title="Add reminder"
                        aria-label={`Add a booster reminder for ${tx.productInfo.name}`}
                        onClick={() => setAddingReminderFor(tx.id)}
                      >
                        <BellPlus />
                      </Button>
                    )}
                    {tx.product && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        title="Print certificate"
                        aria-label={`Print certificate for ${tx.productInfo.name}`}
                        onClick={() => setCertifying(tx)}
                      >
                        <Printer />
                      </Button>
                    )}
                  </div>
                </div>

                <TreatmentAttributes
                  dispensedQuantity={tx.administeredInConsult ? null : tx.productInfo.quantityLabel}
                  administeredQuantity={
                    tx.administeredInConsult ? tx.productInfo.quantityLabel : null
                  }
                  batchNumbers={getAdministeredBatches(tx).map((b) => b.batchNumber)}
                  tagNumber={tx.tagNumber}
                  injectionSite={tx.injectionSite}
                >
                  <span title="Administration date" className="flex items-center gap-1">
                    <Clock className="size-3 shrink-0" />
                    <FormattedDate value={tx.createdAt} clinicId={orderClinicId} />
                  </span>
                  {tx.staff && (
                    <span className="flex items-center gap-1">
                      <Stethoscope className="size-3 shrink-0" />
                      {tx.staff.firstName} {tx.staff.lastName}
                    </span>
                  )}
                </TreatmentAttributes>

                <TreatmentReminders
                  treatment={tx}
                  patientId={patientId}
                  patientSpecies={patientSpecies}
                  addOpen={addingReminderFor === tx.id}
                  onAddOpenChange={(open) => setAddingReminderFor(open ? tx.id : null)}
                />
              </div>
            )
          })
        )}
        {certifying && (
          <VaccineCertificateDialog
            key={certifying.id}
            vaccination={certifying}
            open
            onOpenChange={(open) => !open && setCertifying(null)}
          />
        )}
      </CardContent>
    </Card>
  )
}
